<template>
	<div
		class="hdl-text-response question"
		:class="{
			'has-error': errorMap.size > 0
		}"
	>
		<div ref="rendered" v-html="question.renderedQuestion" />
		<div class="errors" :id="`errs-${question.uuid}`" v-if="errorMessages.length" aria-live="assertive">
			<ul>
				<li v-for="errorMessage of errorMessages" :key="errorMessage">
					{{ errorMessage }}
				</li>
			</ul>
		</div>
		<textarea
			:value="(response)? response : ''"
			@input="input"
			class="input-box-borders"
			ref="textbox"
			:id="question.uuid"
			:aria-labelledby="`${question.uuid}-label`"
			:aria-invalid="errorMap.size > 0"
			:aria-describedby="(errorMessages.length > 0)? `errs-${question.uuid}` : undefined"
			:aria-errormessage="(errorMessages.length > 0)? `errs-${question.uuid}` : undefined"
			:aria-required="question.required"
			@keydown="keydownHandler"
		/>
		<div class="bottom-bar">
			<div class="flex-none">&nbsp;</div>
			<div class="flex-1 flex flex-row justify-end items-center">
				<word-limits-display
					:limit-min="question.limitMin"
					:limit-max="question.limitMax"
					:entered-words="enteredWords"
					:aria-details="question.uuid"
					v-if="hasWordLimits"
				/>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator';
import { PublishedSurveyQuestionType, SurveyResponseErrorCode, SurveyResponseInputErrorType } from '@/common/interfaces';

import { IQuestionComponent } from './types';
import { ResponseGroup } from '../../responses';
import { useResponseStore } from '@/respondants/stores/responseStore';
import { countWords } from '@/common/utils';

@Component({
	emits: ['intentionalBlur']
})
export default class TextResponse extends Vue implements IQuestionComponent {
	@Prop({ required: true })
	question: PublishedSurveyQuestionType;

	response: string | null = null;

	errorMap: Map<SurveyResponseErrorCode, string> = new Map();

	mounted(): void {
		this.$el.__vue__ = this;

		this.errorMap = new Map();

		if (this.responseGroup) {
			const existingResponse = this.responseGroup.getResponse(this.question.uuid);
			this.response = (existingResponse)? existingResponse.text : null;
		}

		this.accessorizeRendered();
	}

	focusFrom(fromDirection: 'above' | 'below'): boolean {
		const textbox = this.$refs.textbox as HTMLTextAreaElement | undefined;

		if (textbox) {
			textbox.focus();
		}

		return true;
	}

	keydownHandler(event: KeyboardEvent): boolean {
		const textbox = event.target as HTMLTextAreaElement;

		const { selectionStart, selectionEnd } = textbox;

		if (selectionStart !== selectionEnd) {
			return false;
		}

		if (event.key === 'ArrowDown' && selectionStart === textbox.value.length) {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'down');
			return true;
		} else if (event.key == 'ArrowUp' && selectionStart === 0) {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'up');
			return true;
		} else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
			event.stopPropagation();
			event.preventDefault();

			this.$emit('intentionalBlur', this.$el, this.question, 'down');
		}

		return false;
	}

	accessorizeRendered(): void {
		const rendered = this.$refs.rendered as HTMLDivElement | undefined;

		if (rendered === undefined) {
			return;
		}

		const label = rendered.querySelector(`label[for="${this.question.uuid}"]`);

		if (label !== null) {
			const labelTail = document.createElement('div');
			labelTail.classList.add('tail');
			label.appendChild(labelTail);

			if (this.question.required) {
				const sroRequiredIndicator = document.createElement('span');
				sroRequiredIndicator.classList.add('hdl-sr-only');
				sroRequiredIndicator.textContent = '(required)';
				labelTail.appendChild(sroRequiredIndicator);
				const requiredIndicator = document.createElement('div');
				requiredIndicator.setAttribute('aria-hidden', 'true');
				requiredIndicator.classList.add('required-indicator');
				requiredIndicator.textContent = '✱';
				labelTail.appendChild(requiredIndicator);
			}
		}
	}

	getResponse(): string | null {
		return this.response;
	}

	hasResponse(): boolean {
		return this.response !== null;
	}

	input(event: Event): void {
		this.response = (event.target as HTMLTextAreaElement).value;
		this.clearError(SurveyResponseErrorCode.MISSING_REQUIRED);

		if (this.responseGroup) {
			this.responseGroup.setResponse(this.question.uuid, this.response, null, null);
		}
	}

	clearError(errorCode: SurveyResponseErrorCode): void {
		this.errorMap.delete(errorCode);
	}

	markError(error: SurveyResponseInputErrorType): void {
		this.errorMap.set(error.code, error.error);
	}

	get errorMessages(): Array<string> {
		return Array.from(this.errorMap.values());
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}

	get enteredWords(): number {
		if (this.response === null) {
			return 0;
		}

		const countedWords = countWords(this.response);

		return countedWords;
	}

	get hasLimitMin(): boolean {
		return this.question.limitMin !== null && this.question.limitMin > 0;
	}

	get hasLimitMax(): boolean {
		return this.question.limitMax !== null && this.question.limitMax > 0;
	}

	get hasWordLimits(): boolean {
		return this.hasLimitMin || this.hasLimitMax;
	}

	@Watch('response')
	watchResponse(): void {
		if (this.hasLimitMax && this.enteredWords < this.question.limitMax!) {
			this.clearError(SurveyResponseErrorCode.OVER_LIMIT);
		}

		if (this.hasLimitMin && this.enteredWords >= this.question.limitMin!) {
			this.clearError(SurveyResponseErrorCode.UNDER_MINIMUM);
		}
	}
}
</script>

<style lang="scss">
.hdl-text-response {
	&.has-error {
		label {
			@apply text-hdl-wc-red;
		}
	}

	.errors {
		@apply text-hdl-wc-red text-sm;

		ul {
			list-style: none;
			margin: 0;
			padding: 0;

			li {
				margin: 0;
				list-style-type: none;
			}
		}
	}

	textarea {
		@apply rounded w-full bg-fill;
		min-height: 100px;

		padding: .35rem .5rem;

		&:focus {
			outline: none;
			--tw-border-opacity: 1;
			border: 1.5px solid var(--main);
			outline: none;
			box-shadow: #535abb25 0 0 0 2px;
		}
	}

	.bottom-bar {
		@apply flex flex-row items-center;
	}
}
</style>
